import {
  InfiniteData,
  UseInfiniteQueryResult,
  UseSuspenseInfiniteQueryResult,
} from "@tanstack/react-query"
import { useInViewport } from "@mantine/hooks"
import { Pagination } from "../../utils/Pagination"
import { DefaultList, DefaultListProps } from "../default-list"
import { Loader } from "@mantine/core"
import { debugLog } from "@/utils/debugLog"

type InfiniteQueryResultFor<TItem> =
  | UseSuspenseInfiniteQueryResult<InfiniteData<Pagination<TItem>, unknown>>
  | UseInfiniteQueryResult<InfiniteData<Pagination<TItem>, unknown>>

type ItemFromInfiniteQueryResult<TQueryResult> =
  TQueryResult extends UseSuspenseInfiniteQueryResult<
    InfiniteData<Pagination<infer U>, unknown>
  >
    ? U
    : TQueryResult extends UseInfiniteQueryResult<
          InfiniteData<Pagination<infer U>, unknown>
        >
      ? U
      : never

export interface DefaultInfiniteListProps<
  TSelectable extends boolean = false,
  TQueryResult extends InfiniteQueryResultFor<unknown> =
    InfiniteQueryResultFor<unknown>,
> extends Omit<
  DefaultListProps<ItemFromInfiniteQueryResult<TQueryResult>, TSelectable>,
  "data"
> {
  queryResult: TQueryResult
}

export function DefaultInfiniteList<
  TSelectable extends boolean = false,
  TQueryResult extends InfiniteQueryResultFor<unknown> =
    InfiniteQueryResultFor<unknown>,
>({
  queryResult,
  ...props
}: DefaultInfiniteListProps<TSelectable, TQueryResult>) {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = queryResult

  const { ref, inViewport } = useInViewport()

  React.useEffect(() => {
    debugLog(
      "inViewport",
      inViewport,
      "hasNextPage",
      hasNextPage,
      "isLoading",
      isLoading,
      "isFetchingNextPage",
      isFetchingNextPage,
      "isError",
      isError
    )
    if (
      inViewport &&
      hasNextPage &&
      !isLoading &&
      !isFetchingNextPage &&
      !isError
    ) {
      fetchNextPage()
    }
  }, [
    inViewport,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    fetchNextPage,
  ])

  const sentinel = React.useMemo(() => {
    if (!hasNextPage || isError) {
      return null
    }

    if (isLoading || isFetchingNextPage) {
      return <Loader />
    }

    return (
      <div
        ref={ref}
        style={{
          height: "100px",
          width: "100%",
          opacity: 0,
          flexShrink: 0,
        }}
      />
    )
  }, [ref, hasNextPage, isLoading, isFetchingNextPage, isError])

  if (!data || !Array.isArray(data.pages)) {
    return null
  }

  // Fallback: leere Seite oder page.data undefiniert
  const items = data.pages.flatMap((page) =>
    Array.isArray(page?.data) ? page.data : []
  ) as ItemFromInfiniteQueryResult<TQueryResult>[]

  return (
    <DefaultList {...props} data={items}>
      {sentinel}
    </DefaultList>
  )
}
